import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Container, 
  Paper, 
  useTheme, 
  useMediaQuery, 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationSettings from '../Settings/NotificationSettings';
import LogViewer from './Logs/LogViewer';
import ClinicManager from './Clinics/ClinicManager';
import ServicePriceManager from './ServicePrices/ServicePriceManager';
import { PractitionerManager } from './Practitioners';
import {
  Notifications as NotificationsIcon,
  Assessment as LogsIcon,
  Business as ClinicIcon,
  AttachMoney as PriceIcon,
  ArrowBack as BackIcon,
  Person as PractitionerIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AdminPanelProps {
  activeTab?: string;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ activeTab: initialTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Convert tab name to index
  const getTabIndex = (tab?: string): number => {
    switch (tab) {
      case 'notifications':
        return 0;
      case 'logs':
        return 1;
      case 'clinics':
        return 2;
      case 'prices':
        return 3;
      case 'practitioners':
        return 4;
      default:
        return 0;
    }
  };
  
  const [activeTab, setActiveTab] = useState(getTabIndex(initialTab));
  const [showDashboard, setShowDashboard] = useState(!initialTab || initialTab === '');

  // Update URL when tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setShowDashboard(false);
    
    // Update URL based on selected tab
    let path = '/admin';
    switch (newValue) {
      case 0:
        path = '/admin/notifications';
        break;
      case 1:
        path = '/admin/logs';
        break;
      case 2:
        path = '/admin/clinics';
        break;
      case 3:
        path = '/admin/prices';
        break;
      case 4:
        path = '/admin/practitioners';
        break;
    }
    
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const handleReturnToDashboard = () => {
    setShowDashboard(true);
    navigate('/admin');
  };

  const handleCardClick = (index: number) => {
    setActiveTab(index);
    setShowDashboard(false);
    
    // Update URL based on selected tab
    let path = '/admin';
    switch (index) {
      case 0:
        path = '/admin/notifications';
        break;
      case 1:
        path = '/admin/logs';
        break;
      case 2:
        path = '/admin/clinics';
        break;
      case 3:
        path = '/admin/prices';
        break;
      case 4:
        path = '/admin/practitioners';
        break;
    }
    
    navigate(path);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!showDashboard && (
            <Tooltip title="Back to Dashboard">
              <IconButton 
                onClick={handleReturnToDashboard}
                sx={{ mr: 1 }}
                size={isMobile ? "small" : "medium"}
              >
                <BackIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            sx={{ fontWeight: 500 }}
          >
            {showDashboard ? 'Admin Dashboard' : 
              activeTab === 0 ? 'Notification Settings' :
              activeTab === 1 ? 'System Logs' :
              activeTab === 2 ? 'Clinic Management' :
              activeTab === 3 ? 'Service Prices' :
              'Practitioner Management'
            }
          </Typography>
        </Box>
      </Box>
      
      {showDashboard ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(0)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%'
                }}>
                  <NotificationsIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: 'primary.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom>
                    Notification Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure email notifications and alerts
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(1)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%'
                }}>
                  <LogsIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: 'info.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom>
                    System Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View system activity and error logs
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(2)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%'
                }}>
                  <ClinicIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: 'success.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom>
                    Clinic Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add and manage clinic locations
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(3)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%'
                }}>
                  <PriceIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: 'warning.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom>
                    Service Prices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure service pricing options
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(4)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%'
                }}>
                  <PractitionerIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: 'secondary.main',
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" gutterBottom>
                    Practitioners
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage practitioners and doctors
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ width: '100%', mb: 4, borderRadius: { xs: 1, md: 2 }, overflow: 'hidden' }}>
          {!isMobile && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="admin tabs"
                variant={isTablet ? "scrollable" : "standard"}
                scrollButtons={isTablet ? "auto" : false}
              >
                <Tab 
                  icon={<NotificationsIcon />} 
                  iconPosition="start" 
                  label="Notification Settings" 
                />
                <Tab 
                  icon={<LogsIcon />} 
                  iconPosition="start" 
                  label="System Logs" 
                />
                <Tab 
                  icon={<ClinicIcon />} 
                  iconPosition="start" 
                  label="Clinic Management" 
                />
                <Tab 
                  icon={<PriceIcon />} 
                  iconPosition="start" 
                  label="Service Prices" 
                />
                <Tab 
                  icon={<PractitionerIcon />} 
                  iconPosition="start" 
                  label="Practitioners" 
                />
              </Tabs>
            </Box>
          )}
          
          <TabPanel value={activeTab} index={0}>
            <NotificationSettings />
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            <LogViewer />
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            <ClinicManager />
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            <ServicePriceManager />
          </TabPanel>
          
          <TabPanel value={activeTab} index={4}>
            <PractitionerManager />
          </TabPanel>
        </Paper>
      )}
    </Container>
  );
};

export default AdminPanel;